import React, { useState, useCallback, useMemo } from 'react';
import { useDossier } from '../dossierEditor/DossierContext';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { generateObjectIdHex } from '../../utils/objectId';
import Trash from '../icons/trash';
import Plus from '../icons/Plus';
import EllipsisVertical from '../icons/EllipsisVertical';

interface IChapter {
  type: string;
  title: string;
  sections: any[];
  _id: string;
}

interface MacadamiaChaptersProps {
  content: IChapter[];
  className?: string;
  itemClassName?: string;
  activeItemClassName?: string;
  onAddChapter: (newChapter: IChapter) => void;
  onEditChapter: (index: number, newTitle: string) => void;
  onDeleteChapter: (index: number) => void;
  onReorderChapters: (updatedChapters: IChapter[]) => void;
}

interface SortableChapterProps {
  chapter: IChapter;
  index: number;
  content: IChapter[];
  currentChapterIndex: number;
  hoveredIndex: number | null;
  confirmDeleteIndex: number | null;
  confirmPosition: { top: number; left: number };
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteClick: (e: React.MouseEvent) => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  getItemClasses: (index: number) => string;
  getTextClasses: (index: number) => string;
}

// Composant Sortable Chapter extrait pour stabilité
const SortableChapter: React.FC<SortableChapterProps> = React.memo(({
  chapter,
  index,
  content,
  currentChapterIndex,
  hoveredIndex,
  confirmDeleteIndex,
  confirmPosition,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onTitleChange,
  onDeleteClick,
  onConfirmDelete,
  onCancelDelete,
  getItemClasses,
  getTextClasses
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: chapter._id,
    data: {
      type: 'chapter',
      chapter
    }
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isActive = currentChapterIndex === index;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${getItemClasses(index)} group relative overflow-hidden`} // relative pour le positionnement absolu de la barre
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Barre latérale absolue pour éviter la distortion */}
      {isActive && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-mcdm-secondary-400 z-10" />
      )}

      <input
        type="text"
        value={chapter.title}
        onChange={onTitleChange}
        className={`bg-transparent border-none outline-none flex-grow w-full px-1 truncate ${getTextClasses(index)}`}
        placeholder="Titre du chapitre"
        title={chapter.title} // Tooltip natif pour le texte tronqué
      />

      {/* Container d'actions visible au survol */}
      <div className={`flex items-center ml-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${isActive ? 'opacity-100' : ''}`}>
        <div className="text-mcdm-third-300 dark:text-mcdm-primary-200 cursor-grab active:cursor-grabbing">
          <EllipsisVertical width="20px" height="20px" />
        </div>

        {content.length > 1 && (
          <button
            className="p-1 text-mcdm-third-300 hover:text-red-500 dark:text-mcdm-primary-200 dark:hover:text-red-400 transition-colors"
            onClick={onDeleteClick}
            title="Supprimer ce chapitre"
          >
            <Trash width="16px" height="16px" />
          </button>
        )}
      </div>

      {confirmDeleteIndex === index && (
        <div
          className="fixed bg-white dark:bg-mcdm-third-400 border rounded shadow-md z-50 p-4"
          style={{
            width: '150px',
            top: `${confirmPosition.top}px`,
            left: `${confirmPosition.left}px`
          }}
        >
          <p className="mb-2 text-mcdm-third-500 dark:text-mcdm-primary-100 text-sm">Confirmer ?</p>
          <div className="flex justify-between">
            <button
              className="text-mcdm-secondary-400 hover:text-mcdm-secondary-500 px-2 py-1 text-sm font-medium"
              onClick={onConfirmDelete}
            >
              Oui
            </button>
            <button
              className="text-red-500 hover:text-red-600 px-2 py-1 text-sm"
              onClick={onCancelDelete}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

const MacadamiaChapters: React.FC<MacadamiaChaptersProps> = ({
  content,
  className = "bg-mcdm-primary-100 dark:bg-mcdm-third-400",
  // Classes par défaut avec padding ajusté pour la barre latérale
  itemClassName = "pl-4 pr-3 py-2 mb-0.5 bg-white dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 hover:shadow-sm transition-all duration-200 border border-transparent",
  activeItemClassName = "bg-mcdm-primary-200 text-mcdm-third-500 dark:text-mcdm-primary-100",
  onAddChapter,
  onEditChapter,
  onDeleteChapter,
  onReorderChapters
}) => {
  const { currentChapterIndex, setCurrentChapterIndex, editMode } = useDossier();
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [confirmPosition, setConfirmPosition] = useState({ top: 0, left: 0 });

  // Mémoïser le handler de changement de titre (avec event input)
  const handleTitleChange = useCallback((index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    onEditChapter(index, e.target.value);
  }, [onEditChapter]);

  // Mémoïser le handler de click de suppression
  const handleDeleteClick = useCallback((e: React.MouseEvent, index: number) => {
    e.stopPropagation();

    // Calculer la position optimale pour la boîte de dialogue
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Positionner à gauche ou à droite selon l'espace disponible
    const left = buttonRect.left < viewportWidth / 2
      ? buttonRect.right + 5 // Positionner à droite du bouton
      : buttonRect.left - 155; // Positionner à gauche du bouton (150px largeur + 5px marge)

    setConfirmPosition({
      top: buttonRect.top,
      left: left
    });
    setConfirmDeleteIndex(index);
  }, []);

  // Mémoïser le handler de confirmation de suppression
  const handleConfirmDelete = useCallback((index: number) => {
    onDeleteChapter(index);
    setConfirmDeleteIndex(null);
  }, [onDeleteChapter]);

  // Mémoïser le handler d'annulation de suppression
  const handleCancelDelete = useCallback(() => {
    setConfirmDeleteIndex(null);
  }, []);

  // Mémoïser la fonction d'ajout de chapitre
  const addNewChapter = useCallback(() => {
    // Génération d'un ID garantie unique en ajoutant un timestamp
    const timestamp = new Date().getTime();
    const newChapter: IChapter = {
      _id: generateObjectIdHex() + '-' + timestamp,
      type: 'chapter',
      title: 'Nouveau chapitre',
      sections: []
    };

    onAddChapter(newChapter);
  }, [onAddChapter]);

  // Mémoïser les fonctions de calcul de classes CSS
  const getItemClasses = useCallback((index: number) => {
    const isActive = currentChapterIndex === index;

    // Classes de base communes - hauteur réduite (h-9 approx 36px)
    // Ajout de pl-4 pour laisser la place à la barre absolue de 4px (w-1)
    const baseClasses = "pl-4 pr-3 py-2 mb-0.5 h-auto min-h-[36px] flex items-center relative hover:shadow-sm transition-all duration-200 border border-transparent";

    if (isActive) {
      // Style actif plus subtil, sans bordure CSS pour éviter le shift
      return `${baseClasses} bg-mcdm-primary-200 dark:bg-mcdm-third-300 shadow-sm`;
    } else {
      // Classes pour les chapitres non actifs
      return `${baseClasses} bg-transparent hover:bg-white dark:hover:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 opacity-80 hover:opacity-100`;
    }
  }, [currentChapterIndex]);

  const getTextClasses = useCallback((index: number) => {
    const isActive = currentChapterIndex === index;

    if (isActive) {
      return 'text-mcdm-third-500 dark:text-mcdm-primary-100 font-medium';
    } else {
      return 'text-mcdm-third-400 dark:text-mcdm-primary-200 font-normal group-hover:text-mcdm-third-500 dark:group-hover:text-mcdm-primary-100';
    }
  }, [currentChapterIndex]);

  // Mémoïser les handlers de survol
  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  // Mémoïser les handlers pour chaque chapitre
  const chapterHandlers = useMemo(() => {
    return content.map((_, index) => ({
      onMouseEnter: () => setHoveredIndex(index),
      onClick: () => setCurrentChapterIndex(index),
      onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => handleTitleChange(index, e),
      onDeleteClick: (e: React.MouseEvent) => handleDeleteClick(e, index),
      onConfirmDelete: () => handleConfirmDelete(index)
    }));
  }, [content.length, setHoveredIndex, setCurrentChapterIndex, handleTitleChange, handleDeleteClick, handleConfirmDelete]);

  // Mémoïser le rendu en mode éditeur
  const editorMode = useMemo(() => {
    const chapterIds = content.map(ch => ch._id);

    return (
      <SortableContext items={chapterIds} strategy={verticalListSortingStrategy}>
        <div className={`${className} p-2 rounded-lg`}>
          {content.map((chapter, index) => {
            const handlers = chapterHandlers[index];
            return (
              <SortableChapter
                key={chapter._id}
                chapter={chapter}
                index={index}
                content={content}
                currentChapterIndex={currentChapterIndex}
                hoveredIndex={hoveredIndex}
                confirmDeleteIndex={confirmDeleteIndex}
                confirmPosition={confirmPosition}
                onMouseEnter={handlers.onMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handlers.onClick}
                onTitleChange={handlers.onTitleChange}
                onDeleteClick={handlers.onDeleteClick}
                onConfirmDelete={handlers.onConfirmDelete}
                onCancelDelete={handleCancelDelete}
                getItemClasses={getItemClasses}
                getTextClasses={getTextClasses}
              />
            );
          })}
          <div
            className="flex justify-center items-center py-2 mt-1 cursor-pointer text-xs text-mcdm-secondary-400 hover:text-mcdm-secondary-500 dark:text-mcdm-secondary-300 dark:hover:text-mcdm-secondary-400 opacity-70 hover:opacity-100 transition-opacity"
            onClick={addNewChapter}
          >
            <Plus width="12px" height="12px" className="mr-2" />
            <span>Nouveau chapitre</span>
          </div>
        </div>
      </SortableContext>
    );
  }, [
    className,
    content,
    currentChapterIndex,
    hoveredIndex,
    chapterHandlers,
    getItemClasses,
    getTextClasses,
    handleMouseLeave,
    confirmDeleteIndex,
    confirmPosition,
    handleCancelDelete,
    addNewChapter
  ]);

  // Mémoïser le rendu en mode lecteur
  const readerMode = useMemo(() => (
    <div className={`${className} p-2 rounded-lg`}>
      {content.map((chapter, index) => {
        const isActive = currentChapterIndex === index;
        const handlers = chapterHandlers[index];

        // Construire les classes sans conflit de fond
        const baseClasses = "pl-4 pr-3 py-2 mb-0.5 h-auto min-h-[36px] flex items-center relative truncate cursor-pointer transition-colors duration-200 border border-transparent";
        const conditionalClasses = isActive
          ? "bg-mcdm-primary-200 dark:bg-mcdm-third-300 font-medium text-mcdm-third-500 dark:text-mcdm-primary-100"
          : "hover:bg-white dark:hover:bg-mcdm-third-300 text-mcdm-third-400 dark:text-mcdm-primary-200 font-normal hover:text-mcdm-third-500 dark:hover:text-mcdm-primary-100 opacity-80 hover:opacity-100";

        return (
          <div
            key={chapter._id}
            className={`${baseClasses} ${conditionalClasses}`}
            onClick={handlers.onClick}
            onMouseEnter={handlers.onMouseEnter}
            onMouseLeave={handleMouseLeave}
            title={chapter.title}
          >
            {/* Barre latérale absolue pour éviter la distortion */}
            {isActive && (
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-mcdm-secondary-400 z-10" />
            )}
            <span className="truncate w-full block">
              {chapter.title}
            </span>
          </div>
        );
      })}
    </div>
  ), [
    className,
    content,
    currentChapterIndex,
    chapterHandlers,
    handleMouseLeave
  ]);

  return editMode === 'editor' ? editorMode : readerMode;
};

// Mémoïsation du composant avec comparaison personnalisée
export default React.memo(MacadamiaChapters, (prevProps, nextProps) => {
  // Comparaison des props simples
  if (
    prevProps.className !== nextProps.className ||
    prevProps.itemClassName !== nextProps.itemClassName ||
    prevProps.activeItemClassName !== nextProps.activeItemClassName ||
    prevProps.onAddChapter !== nextProps.onAddChapter ||
    prevProps.onEditChapter !== nextProps.onEditChapter ||
    prevProps.onDeleteChapter !== nextProps.onDeleteChapter ||
    prevProps.onReorderChapters !== nextProps.onReorderChapters
  ) {
    return false;
  }

  // Comparaison des chapitres
  if (prevProps.content.length !== nextProps.content.length) {
    return false;
  }

  for (let i = 0; i < prevProps.content.length; i++) {
    const prevChapter = prevProps.content[i];
    const nextChapter = nextProps.content[i];

    if (
      prevChapter._id !== nextChapter._id ||
      prevChapter.title !== nextChapter.title ||
      prevChapter.type !== nextChapter.type ||
      JSON.stringify(prevChapter.sections) !== JSON.stringify(nextChapter.sections)
    ) {
      return false;
    }
  }

  return true;
});